import { AssetManager } from 'components/sharpr/assetManager/AssetManager';
import { ApiResourcify } from 'components/sharpr/api/ApiResourcify';
import { ensure } from 'components/sharpr/ensure/ensure';
import { spliceOut } from 'components/sharpr/util/spliceOut';

export function FileReportRoute($stateProvider) {
	'ngInject';
	$stateProvider.state('hub.loggedin.FileReport', {
		url: '/admin/files/report',
		controllerAs: 'vm',
		controller: FileReportCtrl,
		templateUrl: '/ng/hub/File/Report/FileReportCtrl.html?' + window.BUILD,
		permission: ['all'],
		resolve: {
			$style: () => AssetManager.css('/ng/hub/File/Report/FileReportCtrl.css'),
			$title: () => 'File Report',
		},
	});
}

export class FileReportCtrl {
	constructor(Api, Att, modal, $filter) {
		'ngInject';
		this.Api = Api;
		this.Att = Att;
		this.modal = modal;
		this.dateFormat = $filter('shDate');
		this.dateFormatHuman = $filter('shDateHuman');
		this.bytesToText = $filter('shBytesToText');
		this.selectedFiles = [];
		this.table = {
			columns: [
				{ field: 'name', label: 'Filename', sortable: true },
				{ field: 'size', label: 'Size', sortable: true },
				{ field: 'created_at', label: 'Date', sortable: true },
				{ field: 'page_count', label: 'Preview Images', sortable: false },
				{ field: 'hasSummary', label: 'Summary?', sortable: false },
				{ field: 'hasFullText', label: 'Full Text?', sortable: false },
				{ field: 'keyword_count', label: 'Keywords', sortable: false },
				{ field: 'wasScanned', label: 'Virus Scan?', sortable: false },
				{ field: 'numConnectedPosts', label: 'Assoc. Posts', sortable: false },
				{ field: 'indexStatus', label: 'Index Status', sortable: false },
				{ field: 'createdBy', label: 'Uploaded By', sortable: false },
				{ field: 'account_name', label: 'Account', sortable: true },
				{ field: 'hub_name', label: 'Hub', sortable: true },
				{ field: 'id', label: 'File ID', sortable: true },
			],
			actions: [
				{ label: 'Download', icon: 'download2', callback: this.download.bind(this) },
				{ label: 'Preview', icon: 'eye', callback: this.launchPrizmViewer.bind(this) },
				{ label: 'Delete', icon: 'trash2', callback: this.deleteFile.bind(this) },
			],
			checkbox: { field: 'selected', label: '', callback: this.selectFile.bind(this) },
			transformer: this.transform.bind(this),
			image: { field: 'iconHref', width: 16, height: 16, alt: '', label: '' },
			rowAction: this.showDetails.bind(this),
			endpoint: '/files/report',
			limit: 10,
			filters: {},
			bindUrl: 'search',
		};
	}

	download(file) {
		// TODO: make this work with JWT auth
		window.location.href = `/api/v2/files/${file.id}/download`;
	}

	transform(result) {
		result.data.forEach(file => {
			file.iconHref = `/assets/img/filetypes/icon/${file.icon}.png`;
			file.size = this.bytesToText(file.size);
			file.created_at = this.dateFormat(file.created_at, 'long');
			file.created_at_ago = this.dateFormatHuman(file.created_at);
			file.wasScanned = file.scanned ? 'Yes' : 'No';
			file.indexStatus = file.index_status;
			file.hasFullText = file.has_full_text ? 'Yes' : 'No';
			file.hasSummary = file.has_summary ? 'Yes' : 'No';
			file.createdBy = ensure.string(file.created_by.fname) + ' ' + ensure.string(file.created_by.lname);
			file.numConnectedPosts = file.post_count;
		});
		return result;
	}

	refreshTable() {
		this.table.filters.cb = +new Date();
	}

	clearSearch() {
		Object.keys(this.table.filters).forEach(field => {
			delete this.table.filters[field];
		});
	}

	showDetails(file) {
		this.details = file;
		this.enlarged = file;
		const headers = { Hubs: file.hub_id };
		this.Api.get(`/v3/files/${file.id}`, {}, headers).then(details => (this.details = details));
	}

	canCreatePreviews(file) {
		const previewableTypes = ['ppt', 'doc', 'pdf', 'xls'];
		return file && previewableTypes.indexOf(file.icon) > -1;
	}

	canProcessText(file) {
		const extractableTypes = ['ppt', 'doc', 'pdf', 'xls'];
		return file && extractableTypes.indexOf(file.icon) > -1;
	}

	hasPreviewAction(file) {
		return file && file.page_count === 0 && this.canCreatePreviews(file);
	}

	hasTextAction(file) {
		return file && (!file.has_summary || !file.has_full_text || !file.keyword_count) && this.canProcessText(file);
	}

	reprocessDocument(file) {
		file.isReprocessing = true;
		this.Api.post(`/v2/files/${file.id}/reprocess`).then(result => {
			file.isReprocessing = false;
		});
	}

	launchPrizmViewer(file) {
		// get the file info, but send the Hub id so we won't 404
		return this.Api.get(`/v3/files/${file.id}`, {}, { Hubs: file.hub_id }).then(file => {
			return this.Att.popupListing([file]);
		});
	}

	deleteFile(file) {
		this.modal.confirm({ escapedHtml: `Are you sure you want to delete\n${_.escape(file.name)}?` }).then(
			() => {
				this.Api.delete(`/files/${file.id}`).then(
					() => {
						this.refreshTable();
					},
					() => {
						this.modal.alert({ text: 'Error deleting the file.' });
					}
				);
			},
			() => {}
		);
	}

	promptRename(file) {
		this.modal.prompt({ text: 'Enter new name:', inputText: file.name }).then(
			newName => {
				file.name = newName;
				this.Api.patch(`/files/${file.id}`, { name: newName });
			},
			() => {}
		);
	}

	updateSummary(file) {
		this.Api.patch(`/files/${file.id}`, { summary: file.summary });
	}

	updateFullText(file) {
		this.Api.patch(`/files/${file.id}`, { full_text: file.full_text });
	}

	deleteKeyword(file, keyword) {
		const doDelete = () => {
			const idx = file.keywords.indexOf(keyword);
			if (idx > -1) {
				file.keywords.splice(idx, 1);
			}
			this.Api.delete(`/files/${file.id}/keywords/${encodeURIComponent(keyword.keyword)}`);
			this.hasDeletedKeywordsBefore = true;
		};
		if (this.hasDeletedKeywordsBefore) {
			doDelete();
		} else {
			const msg = 'Are you sure you want to delete this keyword?<br><br><strong>This cannot be undone.</strong>';
			this.modal.confirm({ escapedHtml: msg, confirmText: "Yes and don't ask again" }).then(doDelete, () => {});
		}
	}

	selectFile(file) {
		if (file.selected) {
			this.selectedFiles.push(file);
		} else {
			spliceOut(this.selectedFiles, file);
		}
	}

	reprocessSelected() {
		this.isReprocessingSelected = true;
		Promise.all(
			this.selectedFiles.map(file => {
				return this.Api.post(`/v2/files/${file.id}/reprocess`).then(() => {
					file.selected = false;
					spliceOut(this.selectedFiles, file);
				});
			})
		).then(() => {
			this.isReprocessingSelected = false;
		});
	}
}
